import React, { useContext, useState } from 'react';
import { Formik } from 'formik';
import { TextField, Button, Theme, IconButton } from '@mui/material';
import { ValidatePassword, HttpStatuses, ErrorCodes } from '../types';
import { useTranslation } from 'react-i18next';
import makeStyles from '@mui/styles/makeStyles';
import validatepassword from '../lib/services/validatepassword';
import { redirect } from '../lib/Utils';
import { GlobalErrorContext } from '../context';
import Alert from '@mui/lab/Alert';
import ClearIcon from '@mui/icons-material/Clear';

const useStyles = makeStyles((theme: Theme) => ({
  form: {
    width: '100%', // Fix IE 11 issue.
    marginTop: theme.spacing(1)
  },
  submit: {
    margin: theme.spacing(3, 0, 2)
  }
}));

interface Props {
  handleSuccess: (password: string) => void;
  handleFailure: () => void;
}

/**
 * Validate Password Form
 * @param props
 */
export const ValidatePasswordForm: React.FC<Props> = ({
  handleSuccess,
  handleFailure
}) => {
  const classes = useStyles();
  const { t } = useTranslation('translation');
  const { setGlobalError } = useContext(GlobalErrorContext);
  const [notFoundError, setNotFoundError] = useState('');
  const [unauthorisedError, setUnauthorisedError] = useState('');
  return (
    <Formik
      initialValues={{} as ValidatePassword}
      onSubmit={async (values, { setErrors }) => {
        const response = await validatepassword({
          password: values.password
        });
        if (response.error) {
          switch (response.error.code) {
            case ErrorCodes.USER_NOT_FOUND:
              let code = t(response.error.error) ?? t('notFound');
              setNotFoundError(code);
              return;
            case ErrorCodes.INVALID_PASSWORD:
              setErrors({
                password: t('wrongCredentials')
              });
              return;
            case ErrorCodes.UNAUTHORIZED_USER:
              let unauthorisedCode =
                t(response.error.error) ?? t('unauthorised');
              setUnauthorisedError(unauthorisedCode);
              return;
            default:
              setGlobalError(response.error);
              return;
          }
        }

        if (response.status === HttpStatuses.success) {
          if (response.body.valid) {
            handleSuccess(values.password);
          } else {
            redirect('/auth/tryAgain');
          }
        } else {
          handleFailure();
        }
      }}
      validate={values => {
        let errors: { [key: string]: any } = {};
        if (!values.password) {
          errors.password = t('enterValue');
        }
        return errors;
      }}
    >
      {({
        handleSubmit,
        handleChange,
        handleBlur,
        errors,
        isValid,
        isSubmitting,
        values
      }) => (
        <form onSubmit={handleSubmit} className={classes.form} noValidate>
          <TextField
            variant="standard"
            margin="normal"
            required
            fullWidth
            value={values.password}
            label={t('currentPassword')}
            name="password"
            type="password"
            autoFocus
            onChange={handleChange}
            onBlur={handleBlur}
            error={!!errors.password}
            helperText={errors.password}
          />
          <Button
            type="submit"
            fullWidth
            variant="contained"
            color="primary"
            className={classes.submit}
            disabled={isSubmitting || !isValid}
          >
            {t('validatePassword')}
          </Button>
          {notFoundError && (
            <Alert
              severity="error"
              action={
                <IconButton
                  aria-label="close"
                  color="inherit"
                  size="small"
                  onClick={() => {
                    setNotFoundError('');
                  }}
                >
                  <ClearIcon fontSize="inherit" />
                </IconButton>
              }
            >
              {t('notFoundError')}
            </Alert>
          )}
          {unauthorisedError && (
            <Alert
              severity="error"
              action={
                <IconButton
                  aria-label="close"
                  color="inherit"
                  size="small"
                  onClick={() => {
                    setUnauthorisedError('');
                  }}
                >
                  <ClearIcon fontSize="inherit" />
                </IconButton>
              }
            >
              {t('unauthorised')}
            </Alert>
          )}
        </form>
      )}
    </Formik>
  );
};
